// Composable for image formatting
import type { MaybeRef } from '@vueuse/core'

import { proxifyUrl } from '~/features-shared/libs/utils/url-utils'

export enum MEDIA_PROCESSING_STATUS {
  OK = 'processed',
  KO = 'error',
  NOT_PROCESSED = 'non-processed',
  READY = 'ready-to-process',
}

export enum MEDIA_PROCESSING_STATUS_V2 {
  UNDEFINED = 'UNDEFINED',
  QUEUING = 'QUEUING',
  PROCESSING = 'PROCESSING',
  PROCESSED = 'PROCESSED',
  PROCESSING_ERROR = 'PROCESSING_ERROR',
  UNDEFINED_IMPORT = 'UNDEFINED_IMPORT',
  QUEUING_IMPORT = 'QUEUING_IMPORT',
  DISCARDED = 'DISCARDED',
  SKIPPED = 'SKIPPED',
}

export const MAP_MEDIA_PROCESSING_STATUS_V2_TO_V1 = {
  [MEDIA_PROCESSING_STATUS_V2.PROCESSED]: MEDIA_PROCESSING_STATUS.OK,
  [MEDIA_PROCESSING_STATUS_V2.PROCESSING]: MEDIA_PROCESSING_STATUS.NOT_PROCESSED,
  [MEDIA_PROCESSING_STATUS_V2.PROCESSING_ERROR]: MEDIA_PROCESSING_STATUS.KO,
  [MEDIA_PROCESSING_STATUS_V2.DISCARDED]: MEDIA_PROCESSING_STATUS.KO,
  [MEDIA_PROCESSING_STATUS_V2.SKIPPED]: MEDIA_PROCESSING_STATUS.KO,
  [MEDIA_PROCESSING_STATUS_V2.UNDEFINED_IMPORT]: MEDIA_PROCESSING_STATUS.KO,
  [MEDIA_PROCESSING_STATUS_V2.UNDEFINED]: MEDIA_PROCESSING_STATUS.NOT_PROCESSED,
  [MEDIA_PROCESSING_STATUS_V2.QUEUING]: MEDIA_PROCESSING_STATUS.READY,
  [MEDIA_PROCESSING_STATUS_V2.QUEUING_IMPORT]: MEDIA_PROCESSING_STATUS.READY,
}

export enum DIMENSION {
  small = 320,
  medium = 640,
  large = 960,
  M1 = 520,
  // eslint-disable-next-line ts/no-duplicate-enum-values
  L1 = 960,
  XL1 = 1920,
  XXL1 = 3840,
}

function isPictureViewAvailableForV1(status: MEDIA_PROCESSING_STATUS, width: number, height: number) {
  return ((status === MEDIA_PROCESSING_STATUS.OK) && (width > 0) && (height > 0))
  // return ((status === MEDIA_PROCESSING_STATUS.OK))
}

function getDimensionValue(width: number, height: number) {
  return Math.max(width, height)
}

function getConfigFromDimension(dimension: DIMENSION) {
  if (dimension === DIMENSION.XXL1)
    return 'xxl1'
  if (dimension === DIMENSION.XL1)
    return 'xl1'
  if (dimension === DIMENSION.L1)
    return 'l1'
  return 'm1'
}

export function getAvailableMediaViewConfigV1(width: number, height: number) {
  const dimension = getDimensionValue(width, height)

  if (dimension <= DIMENSION.M1)
    return ['o1']

  else if (dimension <= DIMENSION.L1)
    return ['m1', 'o1']

  else if (dimension <= DIMENSION.XL1)
    return ['m1', 'l1', 'o1']

  else if (dimension <= DIMENSION.XXL1)
    return ['m1', 'l1', 'xl1', 'o1']

  else
    return ['m1', 'l1', 'xl1', 'xxl1']
}

export function getBestMediaViewConfigV1(width: number, height: number, key?: DIMENSION) {
  const availableDimensions = getAvailableMediaViewConfigV1(width, height)

  if (!key)
    return availableDimensions.shift()

  else if (availableDimensions.includes(getConfigFromDimension(key)))
    return getConfigFromDimension(key)
  else
    return availableDimensions.pop()
}

function getBestMediaViewConfigV0(dimension = DIMENSION.small) {
  if (dimension === DIMENSION.medium)
    return 'm'
  else if ([DIMENSION.large, DIMENSION.XL1, DIMENSION.L1, DIMENSION.M1].includes(dimension))
    return 'l'
  return 's'
}

// eslint-disable-next-line max-params
export function useMediaFormat(
  subjectUri: MaybeRef<string>,
  width: MaybeRef<number>,
  height: MaybeRef<number>,
  version: MaybeRef<number>,
  status: MaybeRef<MEDIA_PROCESSING_STATUS>,
  swymURL: MaybeRef<string>,
  previewFingerprint: MaybeRef<string | undefined> = '',
  key?: MaybeRef<DIMENSION>,
) {
  const config = computed(() => {
    if (isPictureViewAvailableForV1(toValue(status), toValue(width), toValue(height)))
      return getBestMediaViewConfigV1(toValue(width), toValue(height), toValue(key))

    else
      return getBestMediaViewConfigV0(toValue(key))
  })

  const url = computed(() => {
    const updateFingerPrint = toValue(previewFingerprint) ? `/update/${toValue(previewFingerprint)}` : ''
    const baseUrl = `${toValue(swymURL)}/api/media/streammedia/id/${toValue(subjectUri)}/type/`
    if (toValue(status) !== MEDIA_PROCESSING_STATUS.OK)
      return proxifyUrl(`${baseUrl}thumb/key/l_thumb${updateFingerPrint}`)

    if (isPictureViewAvailableForV1(toValue(status), toValue(width), toValue(height)))
      return proxifyUrl(`${baseUrl}picture/key/${toValue(config)}${updateFingerPrint}`)

    return proxifyUrl(`${baseUrl}thumb/key/${toValue(config)}_thumb${updateFingerPrint}`)
  })

  return { config, url }
}
